import React from 'react';
import { Box, Container, Grid, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Seo from '../common/Seo';

const principles = [
	{
		title: 'Practical modernization',
		description: 'We improve what is already working, remove what is slowing you down, and avoid wasteful rebuilds.',
	},
	{
		title: 'Systems-first thinking',
		description: 'Product, infrastructure, automation, and user experience are treated as one operating system for the business.',
	},
	{
		title: 'Calm, senior delivery',
		description: 'Clients get thoughtful communication, crisp execution, and technical decisions explained in business terms.',
	},
];

const About = () => {
	return (
		<>
			<Seo
				title="About - BrokenCosmos Labs"
				description="Learn how BrokenCosmos Labs supports Toronto and Canadian teams with software modernization, systems integration, and dependable delivery."
				path="/about"
			/>
			<Box id="about" sx={{ py: { xs: 10, md: 14 } }}>
				<Container maxWidth="xl">
					<Grid container spacing={{ xs: 5, md: 7 }} alignItems="center">
						<Grid item xs={12} lg={6}>
							<Typography variant="overline" sx={{ color: '#93c5fd', letterSpacing: '0.12em' }}>
								About BrokenCosmos Labs
							</Typography>
							<Typography variant="h2" component="h1" sx={{ mt: 1.5, mb: 2.5, maxWidth: '11ch' }}>
								We help digital businesses grow up gracefully.
							</Typography>
							<Typography variant="body1" sx={{ mb: 2.5 }}>
								BrokenCosmos Labs was built around a simple idea: the best software work does not just add features, it
								reduces friction across the whole business. That means cleaner architecture, clearer delivery, better
								integration between tools, and a more polished experience for the people relying on the product.
							</Typography>
							<Typography variant="body1" sx={{ mb: 2.5 }}>
								Our work spans application engineering, DevOps, automation, and product refinement. We step in where
								teams need sharper technical direction, stronger implementation quality, or a more professional layer over
								existing systems.
							</Typography>
							<Typography variant="body1">
								The result is software that feels steadier, teams that move with less drag, and client-facing experiences
								that reflect a more mature brand.
							</Typography>
						</Grid>
						<Grid item xs={12} lg={6}>
							<Paper
								elevation={0}
								sx={{
									p: { xs: 3, md: 4 },
									borderRadius: '28px',
									background: 'linear-gradient(180deg, rgba(15,23,42,0.82) 0%, rgba(15,27,45,0.72) 100%)',
								}}
							>
								<Box
									sx={{
										p: { xs: 3, md: 4 },
										borderRadius: '24px',
										background: 'linear-gradient(135deg, rgba(37,99,235,0.2) 0%, rgba(20,184,166,0.08) 100%)',
										border: `1px solid ${alpha('#e2e8f0', 0.08)}`,
										mb: 3,
									}}
								>
									<Typography variant="h3" sx={{ mb: 1.5 }}>
										Professional by design
									</Typography>
									<Typography variant="body2">
										Every engagement is structured to leave the product more understandable, more reliable, and easier to extend.
									</Typography>
								</Box>
								<Grid container spacing={2.2}>
									{principles.map((principle) => (
										<Grid item xs={12} key={principle.title}>
											<Box
												sx={{
													p: 2.5,
													borderRadius: '20px',
													backgroundColor: alpha('#08101d', 0.68),
													border: `1px solid ${alpha('#e2e8f0', 0.06)}`,
												}}
											>
												<Typography variant="h6" sx={{ mb: 1 }}>
													{principle.title}
												</Typography>
												<Typography variant="body2">{principle.description}</Typography>
											</Box>
										</Grid>
									))}
								</Grid>
							</Paper>
						</Grid>
					</Grid>
				</Container>
			</Box>
		</>
	);
};

const AboutPage = () => (
	<>
		<About />
	</>
);

export default AboutPage;
